import React, { useState, useEffect, useMemo } from "react";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { IconButton } from "@mui/material";
import axios from "axios";
import { Switch } from "@mui/material";
import PDF from "../../../../src/shared/assets/icons/PDF.png";
import JPG from "../../../../src/shared/assets/icons/JPG.png";
import XLS from "../../../../src/shared/assets/icons/XLS.png";
import DOC from "../../../../src/shared/assets/icons/DOC.png";
import PNG from "../../../../src/shared/assets/icons/PNG.png";
import GlobalSnackbar from "../../../shared/utils/GlobalSnackbar";

import FilePreviewModal from "../../../shared/utils/FilePreviewModal";
import getAddressLatLong from "../../../shared/utils/getAddressLatLong";

const rows = [
  {
    label: "Material Cost",
    amount: "materialCostAmount",
    area: "materialCostArea",
  },
  { label: "Recce Cost", amount: "recceCostAmount", area: "recceCostArea" },
  {
    label: "Remote Location Cost",
    amount: "remoteLocationCostAmount",
    area: "remoteLocationCostArea",
  },
  {
    label: "Small Order Size",
    amount: "smallOrderSizeAmount",
    area: "smallOrderSizeArea",
  },
  {
    label: "Installation(s)",
    amount: "installationCostAmount",
    area: "installationCostArea",
  },
];

// utility fn
const calculateRate = (amount, area) =>
  area > 0 ? parseFloat(amount || 0) / parseFloat(area || 1) : 0;

const CostCalculator = ({
  operation,
  snackbar,
  existingQuotation = {},
  activeProduct,
  setSnackbar,
  productIndex,
  setOpenCalculator,
  onSubmitChanges,
  totalArea,
  handleTotalAreaChange,
  setLocationDetails,
  locationDetails,
  setViewMapToggle,
  viewMapToggle,
}) => {
  // local state for all fields
  const [localData, setLocalData] = useState({
    materialCostAmount: existingQuotation?.materialCostAmount || "",
    materialCostArea: existingQuotation?.materialCostArea || "",
    recceCostAmount: existingQuotation?.recceCostAmount || "",
    recceCostArea: existingQuotation?.recceCostArea || "",
    remoteLocationCostAmount: existingQuotation?.remoteLocationCostAmount || "",
    remoteLocationCostArea: existingQuotation?.remoteLocationCostArea || "",
    smallOrderSizeAmount: existingQuotation?.smallOrderSizeAmount || "",
    smallOrderSizeArea: existingQuotation?.smallOrderSizeArea || "",
    installationCostAmount: existingQuotation?.installationCostAmount || "",
    installationCostArea: existingQuotation?.installationCostArea || "",
    marginValue: existingQuotation?.marginValue || "",
    bomUploaded: existingQuotation?.bomUploaded || "",
    totalCostOfProduct: existingQuotation?.totalCostOfProduct || "",
  });

  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploadedFileUrl, setUploadedFileUrl] = useState("");
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  // handlers

  useEffect(() => {
    if (viewMapToggle && locationDetails?.locations?.length > 0) {
      const fetchCoordinates = async () => {
        const results = await Promise.all(
          locationDetails.locations.map(async (loc) => {
            const { address, city, state, zipCode } = loc.details;
            const coords = await getAddressLatLong(
              address,
              city,
              state,
              zipCode,
            );
            return { ...loc, locationCoordinates: coords };
          }),
        );
        setLocationDetails({ locations: results });
      };

      fetchCoordinates();
    }
  }, [viewMapToggle]);
  const cityWiseData = useMemo(() => {
    return getCityDataArray(locationDetails, activeProduct);
  }, []);

  useEffect(() => {
    const totalAreaSqFt = cityWiseData?.reduce(
      (total, city) => total + (Number(city.totalAreaSqFt) || 0),
      0,
    );
    if (totalAreaSqFt) {
      handleTotalAreaChange(totalAreaSqFt);
    }
  }, [cityWiseData]);

  const handleLocalChange = (field, value) => {
    setLocalData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    if (onSubmitChanges) {
      onSubmitChanges(localData, productIndex);
      setOpenCalculator(null);
      setSnackbar({
        open: true,
        message: "Changes Save Successfully",
        severity: "success",
      });
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // ✅ File size validation (5MB = 5 * 1024 * 1024 bytes)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setSnackbar({
        open: true,
        message: "Maximum (2 mb) limit exceed",
        severity: "error",
      });
      return; // stop execution
    }

    try {
      const formDataNew = new FormData();
      formDataNew.append("files", file);
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${process.env.REACT_APP_URL}/upload`,
        formDataNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.success) {
        const uploaded = response?.data?.data[0];
        setLocalData((prev) => ({ ...prev, bomUploaded: uploaded }));
        setSnackbar({
          open: true,
          message: "File upload successfully",
          severity: "success",
        });
      }
    } catch (err) {
      console.error("Error uploading file", err);
    }
  };

  const handlePreviewClick = () => {
    if (localData.bomUploaded) {
      setUploadedFileUrl(localData.bomUploaded);
      setPreviewOpen(true);
    }
  };

  // file icons
  const getFileIcon = (ext) => {
    const iconMap = {
      pdf: PDF,
      jpeg: JPG,
      jpg: JPG,
      png: PNG,
      xls: XLS,
      xlsx: XLS,
      doc: DOC,
      docx: DOC,
    };
    return iconMap[ext] || DOC;
  };
  // total cost calculation
  const totalCost =
    rows.reduce(
      (sum, r) => sum + calculateRate(localData[r.amount], localData[r.area]),
      0,
    ) *
    (1 + (parseFloat(localData.marginValue) || 0) / 100);

  useEffect(() => {
    setLocalData((prev) => ({
      ...prev,
      totalCostOfProduct: totalCost,
    }));
  }, [totalCost]);

  useEffect(() => {
    const updatedData = { ...localData };
    rows.forEach((r) => {
      updatedData[r.area] = totalArea > 0 ? totalArea : "";
    });
    setLocalData(updatedData);
  }, [totalArea]);

  return (
    <div className="flex flex-col xl:flex-row">
      <div className="w-3/4 p-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Product: {activeProduct}</h2>
          {/* <button className="text-blue-600 text-sm font-medium flex border border-blue-600 p-2 rounded items-center gap-1">
            Download Cost
            <FileDownloadOutlinedIcon />
          </button> */}
          {localStorage.getItem("role") != 0 && <p>Total Area : {totalArea}</p>}
        </div>
        {localStorage.getItem("role") == 3 ||
        localStorage.getItem("role") == 0 ? (
          <div></div>
        ) : (
          <div className="border">
            {/* Table Headers */}
            <div className="grid grid-cols-4 text-gray-400 font-semibold text-sm uppercase mt-4 mb-2 px-10">
              <div>Process</div>
              <div>Amount</div>
              <div>Area</div>
              <div>Cost per Sq. Ft.</div>
            </div>

            {/* Rows */}
            {rows.map((r, idx) => (
              <div className="px-10" key={idx}>
                <div className="grid grid-cols-4 items-center gap-2 py-4 border-b border-black text-sm">
                  <div className="font-medium">{r.label}</div>

                  {/* Amount */}

                  <input
                    type="number"
                    value={localData[r.amount]}
                    onWheel={(e) => e.currentTarget.blur()} // or: (e) => e.preventDefault()
                    min={0}
                    onChange={(e) =>
                      handleLocalChange(r.amount, e.target.value)
                    }
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    className="bg-[#E8F0FE] text-gray-700 px-3 py-3 rounded w-36 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    disabled={operation === "view"} // ✅ condition
                  />

                  {/* Area */}

                  <input
                    type="number"
                    onWheel={(e) => e.currentTarget.blur()}
                    min={0}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    value={localData[r?.area]}
                    onChange={(e) => handleLocalChange(r.area, e.target.value)}
                    className="bg-[#E8F0FE] text-gray-700 px-3 py-3 rounded w-36 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    disabled={operation === "view" || totalArea > 0}
                  />

                  {/* Rate */}
                  <div className="text-black font-medium">
                    Rs{" "}
                    {calculateRate(
                      localData[r.amount],
                      localData[r.area],
                    ).toFixed(2)}
                  </div>
                </div>
              </div>
            ))}

            {/* Margin Row */}
            <div className="grid grid-cols-4 items-center gap-2 py-2 text-sm px-10">
              <div className="font-medium">Margin in % age</div>
              <div></div>
              <div></div>
              <input
                disabled={operation == "view"}
                type="number"
                onWheel={(e) => e.currentTarget.blur()} // or: (e) => e.preventDefault()
                min={0}
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-", "."].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                value={localData.marginValue}
                onChange={(e) =>
                  handleLocalChange("marginValue", e.target.value)
                }
                className="bg-[#E8F0FE] text-gray-400 px-3 py-3 rounded w-36"
              />
            </div>

            {/* Total */}
            <div className="flex justify-between items-center bg-gray-100 font-semibold px-10 py-4 mt-2 text-sm">
              <span className="text-black uppercase">Total</span>
              <span className="text-black mr-32">
                Rs {totalCost.toFixed(2)}
              </span>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end m-4">
              {operation != "view" && (
                <button
                  onClick={handleSubmit}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg shadow"
                >
                  Save Changes
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Upload Section */}
      {localStorage.getItem("role") == 3 ||
      localStorage.getItem("role") == 0 ? (
        <div></div>
      ) : (
        <div className="w-1/3">
          <div className=" bg-white p-10 rounded-lg shadow h-96 mt-[4.6rem]">
            <h3 className="text-gray-400 font-bold mb-6">UPLOAD DOCUMENT</h3>
            {operation != "view" ? (
              <div className="flex flex-col items-center justify-evenly border-2 border-dashed border-gray-300 rounded-md p-2 w-full h-40 text-center cursor-pointer hover:border-blue-500 transition">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-evenly mb-2">
                  <img src={PDF} alt="PDF" className="w-6 h-6" />
                </div>
                <div className="text-blue-600 font-medium text-xs">
                  <label htmlFor="uploadBom" className="cursor-pointer">
                    Attach BOM Document
                  </label>
                  <input
                    accept=".pdf,.doc,.xls,.xlsx,.png,.jpg,.jpeg,.docx"
                    id="uploadBom"
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleFileUpload}
                  />
                </div>
                <p className="text-black-600 text-xs">(Max. File size: 2 MB)</p>
              </div>
            ) : localData.bomUploaded ? (
              localData.bomUploaded && (
                <div className="w-full mt-4 space-y-2">
                  <div className="flex items-center justify-between border-2 border-gray-200 rounded px-4 py-2 bg-white">
                    <div className="flex justify-between items-center w-full">
                      <img
                        src={getFileIcon(
                          localData.bomUploaded.split(".").pop().toLowerCase(),
                        )}
                        alt="file"
                        style={{
                          width: 35,
                          height: 35,
                          objectFit: "contain",
                          cursor: "pointer",
                        }}
                        onError={(e) => {
                          e.target.src = DOC;
                        }}
                      />
                      <div className="flex flex-col max-w-[200px] truncate">
                        <span
                          className="text-sm truncate"
                          title={localData.bomUploaded.split("/").pop()}
                        >
                          {localData.bomUploaded.split("/").pop()}
                        </span>
                      </div>

                      <IconButton onClick={handlePreviewClick}>
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <span className=" bg-gray-500 rounded-md p-2 text-white">
                There is no File upload
              </span>
            )}

            {localData.bomUploaded && (
              <div className="w-full mt-4 space-y-2">
                <div className="flex items-center justify-between border-2 border-gray-200 rounded px-4 py-2 bg-white">
                  <div className="flex justify-between items-center w-full">
                    <img
                      src={getFileIcon(
                        localData.bomUploaded.split(".").pop().toLowerCase(),
                      )}
                      alt="file"
                      style={{
                        width: 35,
                        height: 35,
                        objectFit: "contain",
                        cursor: "pointer",
                      }}
                      onError={(e) => {
                        e.target.src = DOC;
                      }}
                    />
                    <div className="flex flex-col max-w-[200px] truncate">
                      <span
                        className="text-sm truncate"
                        title={localData.bomUploaded.split("/").pop()}
                      >
                        {localData.bomUploaded.split("/").pop()}
                      </span>
                    </div>

                    <IconButton onClick={handlePreviewClick}>
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                  </div>
                </div>
                <GlobalSnackbar
                  snackbar={snackbar}
                  handleClose={handleSnackbarClose}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <FilePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fileUrl={uploadedFileUrl}
        fileName={"BOM File"}
      />
    </div>
  );
};

export default CostCalculator;

const getCityDataArray = (data, productType) => {
  if (!data || !Array.isArray(data.locations)) return [];

  const cityMap = {};

  data.locations.forEach((location, index) => {
    const city = location?.details?.city || "Unknown";
    const phone =
      location?.details?.storeOwnerPhoneNumber || `__no_phone__${index}`;
    const coords = location?.locationCoordinates || {};
    const product = location?.product || {};
    const productName = product?.productType || "Unknown";

    // area (ensure numeric)
    const productAreaSqFt =
      (Number(product?.productHeight) || 0) *
      (Number(product?.productWidth) || 0);

    // If a productType filter is provided and this location is not that product, skip it.
    if (productType && productName !== productType) return;

    const formattedLocation = {
      id: index + 1,
      locationCoordinates: {
        latitude: coords.lat,
        longitude: coords.lng,
      },
      product,
    };

    if (!cityMap[city]) {
      cityMap[city] = {
        sites: 0,
        locations: new Map(),
        totalAreaSqFt: 0,
      };
    }

    const entry = cityMap[city];

    // count a site once per unique phone
    if (!entry.locations.has(phone)) {
      entry.locations.set(phone, formattedLocation);
      entry.sites += 1;
    }

    // accumulate area
    entry.totalAreaSqFt += productAreaSqFt;
  });

  // Convert to array
  return Object.entries(cityMap).map(([name, value]) => ({
    name,
    sites: value.sites,
    locations: Array.from(value.locations.values()),
    // NOTE: when productType was passed, totalAreaSqFt here equals total for that product only
    totalAreaSqFt: value.totalAreaSqFt,
  }));
};
